import { useEffect, useState } from 'react';

import type { Issue } from '@tasks/contracts';

import { MarkdownView } from '../../components/MarkdownView';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { MarkdownEditor } from '../../components/MarkdownEditor';
import styles from './IssueDescriptionTab.module.css';

export type IssueDescriptionTabProps = {
  readonly issue: Issue;
  readonly canEdit: boolean;
  readonly onSave: (description: string) => void;
  readonly isSaving: boolean;
};

/** Описание: markdown, сохранение по blur (docs/06 §3.2). */
export function IssueDescriptionTab({ issue, canEdit, onSave, isSaving }: IssueDescriptionTabProps) {
  const [isEditing, setEditing] = useState(false);
  const [draft, setDraft] = useState(issue.description);
  const debouncedDraft = useDebouncedValue(draft, 400);

  useEffect(() => {
    setDraft(issue.description);
  }, [issue.id, issue.description]);

  // Автосохранение: отдельный effect, чтобы не терять правки при закрытии панели.
  useEffect(() => {
    if (!isEditing || !canEdit) {
      return;
    }
    if (debouncedDraft === issue.description) {
      return;
    }
    onSave(debouncedDraft);
  }, [debouncedDraft, isEditing, canEdit]);

  if (!canEdit) {
    return <MarkdownView value={issue.description} />;
  }

  if (!isEditing) {
    return (
      <div
        className={styles['readonly']}
        role="button"
        tabIndex={0}
        onClick={() => setEditing(true)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            setEditing(true);
          }
        }}
      >
        <MarkdownView value={issue.description} />
        <span className={styles['hint']}>Нажмите, чтобы изменить описание</span>
      </div>
    );
  }

  return (
    <MarkdownEditor
      value={draft}
      autoFocus
      minRows={8}
      placeholder="Опишите задачу. Поддерживается markdown."
      ariaLabel="Описание задачи"
      isSubmitting={isSaving}
      onChange={setDraft}
      onSubmit={() => {
        onSave(draft);
        setEditing(false);
      }}
      onCancel={() => {
        setDraft(issue.description);
        setEditing(false);
      }}
    />
  );
}
